import React, { useRef, useEffect, useMemo } from 'react';
import useResizeObserver from 'use-resize-observer';

import useAppContext from 'flow/hooks/useAppContext';
import FlowIcon from 'flow/components/custom-components/flow-icon';

import { makeSelectCoListenerContactsByCallId } from 'flow/selectors/call-selectors';
import { getContactName } from 'flow/utils/contact-utils';
import Tooltip from '../ui-components/Tooltip';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import { List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import { i18n } from 'i18next';

const getNames = (contacts: List<TypedMap<Contact>>, namesToShow: number) => {
    if (contacts.size === 1) {
        return getContactName(contacts.first());
    }

    const contactsToShow = contacts.setSize(namesToShow);
    const numAdditionalContacts = contacts.size - contactsToShow.size;
    return (
        contactsToShow.map((contact) => contact.get('firstName')).join(', ') +
        (numAdditionalContacts > 0 ? ' +' + numAdditionalContacts : '')
    );
};

const getTooltipString = (contacts: List<TypedMap<Contact>>, i18next: i18n) => {
    const listOfNames = contacts
        .setSize(contacts.size - 1)
        .map((contact) => getContactName(contact))
        .join(', ');
    const lastName = getContactName(contacts.last());
    return i18next.t(
        'callcontrol.colistening.colistenernotice_plural_with_separator',
        { Names: listOfNames, Lastname: lastName },
    );
};

interface Props {
    callId: string;
}

const CoListeningSubjectBanner = ({ callId }: Props) => {
    const { i18next } = useAppContext();

    const containerRef = useRef(null);
    const itemRef = useRef(null);

    const selectCoListenerContactsByCallId = useMemo(
        makeSelectCoListenerContactsByCallId,
        [],
    );

    const coListeningContacts = useAppSelector((state) =>
        selectCoListenerContactsByCallId(state, callId),
    );

    const { width: itemWidth } = useResizeObserver({
        ref: coListeningContacts ? itemRef : null,
    });

    const beforeNameRef = useRef(null);
    const nameRef = useRef(null);
    const afterNameRef = useRef(null);

    useEffect(() => {
        if (!coListeningContacts || coListeningContacts.isEmpty()) {
            return;
        }
        let numNamesToShow = coListeningContacts.size;
        // eslint-disable-next-line no-constant-condition
        while (numNamesToShow > 0) {
            const names = getNames(coListeningContacts, numNamesToShow);
            const text =
                numNamesToShow > 1
                    ? i18next.t(
                          'callcontrol.colistening.colistenernotice_plural',
                          { Names: names },
                      )
                    : i18next.t('callcontrol.colistening.colistenernotice', {
                          Name: names,
                      });
            const nameIndex = text.indexOf(names);
            nameRef.current.innerText = names;
            beforeNameRef.current.innerText = text.substr(0, nameIndex);
            afterNameRef.current.innerText = text.substr(
                nameIndex + names.length,
            );

            const isOverflow =
                itemRef.current.scrollWidth > itemRef.current.offsetWidth;
            if (!isOverflow) {
                break;
            }
            numNamesToShow--;
        }
    }, [coListeningContacts, itemWidth, itemRef, i18next]);

    if (!coListeningContacts || coListeningContacts.isEmpty()) {
        return null;
    }

    const showTooltip = coListeningContacts.size !== 1;

    const getBanner = () => {
        return (
            <div
                ref={containerRef}
                className="bg-yellow flex h-9 w-full items-center rounded-sm px-2 text-gray-800"
            >
                <FlowIcon uniqueId="hearing" />
                <div
                    ref={itemRef}
                    className="flex grow gap-x-1 truncate text-xs"
                >
                    <span ref={beforeNameRef} />
                    <span ref={nameRef} className="font-bold" />
                    <span ref={afterNameRef} />
                </div>
            </div>
        );
    };

    return (
        <>
            {showTooltip ? (
                <Tooltip
                    label={getTooltipString(coListeningContacts, i18next)}
                    placement="top"
                >
                    {getBanner()}
                </Tooltip>
            ) : (
                getBanner()
            )}
        </>
    );
};

export default CoListeningSubjectBanner;
